import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// Auth gating is handled upstream by <RequireAuth> in the router (App.tsx).
import { isFirstPass } from "@/lib/analytics/attempts";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { ActivityHeatmap } from "@/components/charts/activity-heatmap";
import { DurationTrend } from "@/components/charts/duration-trend";
import { RunsOverTime } from "@/components/charts/runs-over-time";
import { QueueTimeTrend } from "@/components/charts/queue-time-trend";
import { RunnerOSBreakdownChart } from "@/components/charts/runner-os-breakdown";
import { SuccessRateTrend } from "@/components/charts/success-rate-trend";
import { AttemptsModeToggle } from "@/components/panels/attempts-mode-toggle";
import { BranchBreakdown } from "@/components/panels/branch-breakdown";
import { BucketToggle } from "@/components/panels/bucket-toggle";
import { EvolutionMatrix } from "@/components/panels/evolution-matrix";
import { CapWarningBanner } from "@/components/panels/cap-warning-banner";
import { CompareToggle } from "@/components/panels/compare-toggle";
import { DateRangePicker } from "@/components/panels/date-range-picker";
import { EmptyState } from "@/components/panels/empty-state";
import { FailureAnalysis } from "@/components/panels/failure-analysis";
import { OverviewCards } from "@/components/panels/overview-cards";
import { ProgressBanner } from "@/components/panels/progress-banner";
import { RateLimitBanner } from "@/components/panels/rate-limit-banner";
import { SlowestJobs } from "@/components/panels/slowest-jobs";
import { TriggerEventsChart } from "@/components/panels/trigger-events-chart";
import { WorkflowsTable } from "@/components/panels/workflows-table";

import {
  computeActivityHeatmap,
  computeBranchStats,
  computeDurationTrend,
  computeFailureBreakdown,
  computeRunsOverTime,
  computeOverview,
  computeRunnerOSBreakdown,
  computeSlowestJobs,
  computeSuccessRateTrend,
  computeQueueTimeTrend,
  computeTriggerEvents,
  computeWorkflowStats,
} from "@/lib/analytics/metrics";
import { computeEvolutionMatrix } from "@/lib/analytics/evolution";
import { enumeratePeriods, resolveBucket } from "@/lib/analytics/buckets";
import type { AttemptsMode, BucketPref, BucketSize } from "@/lib/analytics/types";
import type { Job, WorkflowRun, WorkflowRunUsage } from "@/lib/github/types";
import { useAuth } from "@/lib/auth/use-auth";
import { mapConcurrent, useOctokit, usePreviousRuns, useRuns, useWorkflows } from "@/lib/github/queries";
import type { WindowDays } from "@/lib/github/types";
import { useRateLimit } from "@/lib/github/rate-limit";

interface PerRunJobs {
  run: WorkflowRun;
  jobs: Job[];
  workflowName: string | null;
}

// Stable empty-array reference — passing `query.data ?? []` would produce a new
// reference per render, invalidating every downstream useMemo unnecessarily
// during the initial fetch.
const EMPTY_RUNS: WorkflowRun[] = [];

export function Dashboard() {
  const params = useParams<{ owner: string; name: string }>();
  const owner = params.owner ?? "";
  const name = params.name ?? "";
  const navigate = useNavigate();
  const { token } = useAuth();
  const octokit = useOctokit(token);

  const rateLimit = useRateLimit(octokit);
  const paused = rateLimit.data?.exhausted === true;

  const [windowDays, setWindowDays] = useState<WindowDays>(7);

  const workflowsQ = useWorkflows(paused ? null : octokit, owner, name);
  const runsQ = useRuns(paused ? null : octokit, owner, name, { windowDays });

  const [utc, setUtc] = useState(false);
  // Toggle: default "first-pass" (exclude re-runs). Resets per session.
  const [attemptsMode, setAttemptsMode] = useState<AttemptsMode>("first-pass");
  // Bucket preference: "auto" = adaptive per windowDays, or forced day/week/month.
  const [bucketPref, setBucketPref] = useState<BucketPref>("auto");

  // Compare mode: overlay previous period data. Off by default, resets per session.
  const [compareMode, setCompareMode] = useState(false);

  // Resolve the effective bucket from preference + windowDays.
  const bucket: BucketSize = bucketPref === "auto" ? resolveBucket(windowDays) : bucketPref;

  // Previous-period fetch — only when compareMode is on.
  // Pass `null` as options when off so the hook is a no-op.
  const prevRunsQ = usePreviousRuns(
    paused ? null : octokit,
    owner,
    name,
    compareMode ? { windowDays } : null,
  );

  // Heavy lazy fetches (slowest-jobs + runner-os) — triggered by user click.
  const [perRunJobs, setPerRunJobs] = useState<PerRunJobs[] | null>(null);
  const [timings, setTimings] = useState<WorkflowRunUsage[] | null>(null);
  const [lazyLoading, setLazyLoading] = useState(false);

  // Reset lazy data when window changes — avoids stale data from previous window
  // being mixed with new runs.
  useEffect(() => {
    setPerRunJobs(null);
    setTimings(null);
  }, [windowDays]);

  async function loadHeavyData() {
    if (!octokit || !runsQ.runs || runsQ.runs.length === 0) return;
    setLazyLoading(true);
    try {
      const wfNameById = new Map(
        (workflowsQ.data ?? []).map((w) => [w.id, w.name ?? w.path ?? null]),
      );
      const topByDuration = [...runsQ.runs]
        .map((r) => {
          const start = r.run_started_at ?? r.created_at;
          const end = r.updated_at;
          const ms =
            start && end
              ? Math.max(0, Date.parse(end) - Date.parse(start))
              : 0;
          return { run: r, ms };
        })
        .sort((a, b) => b.ms - a.ms)
        .slice(0, 20);

      const MAX_JOB_PAGES = 3; // guard against 300+ jobs per run eating rate-limit
      const jobsResults = await mapConcurrent(
        topByDuration,
        async ({ run }) => {
          const jobs: Job[] = [];
          for (let page = 1; page <= MAX_JOB_PAGES; page++) {
            const res = await octokit.rest.actions.listJobsForWorkflowRun({
              owner,
              repo: name,
              run_id: run.id,
              per_page: 100,
              page,
            });
            jobs.push(...((res.data.jobs as Job[]) ?? []));
            if (res.data.jobs.length < 100) break;
          }
          return {
            run,
            jobs,
            workflowName:
              (typeof run.workflow_id === "number"
                ? wfNameById.get(run.workflow_id)
                : null) ?? null,
          } satisfies PerRunJobs;
        },
        5,
      );
      setPerRunJobs(jobsResults);

      const usages = await mapConcurrent(
        topByDuration,
        async ({ run }) => {
          const res = await octokit.rest.actions.getWorkflowRunUsage({
            owner,
            repo: name,
            run_id: run.id,
          });
          return res.data as WorkflowRunUsage;
        },
        5,
      );
      setTimings(usages);
    } finally {
      setLazyLoading(false);
    }
  }

  const runs = runsQ.runs ?? EMPTY_RUNS;
  const workflows = workflowsQ.data ?? [];

  // Single filter pass shared by all compute* that only need mode-filtered runs.
  // computeOverview and computeWorkflowStats still receive (runs, mode) because
  // they also need the unfiltered set (rerunRate, allRunsByWorkflow).
  const filteredRuns = useMemo(
    () => (attemptsMode === "first-pass" ? runs.filter(isFirstPass) : runs),
    [runs, attemptsMode],
  );

  const overview = useMemo(
    () => computeOverview(runs, attemptsMode),
    [runs, attemptsMode],
  );
  const workflowStats = useMemo(
    () => computeWorkflowStats(runs, workflows, attemptsMode, bucket),
    [runs, workflows, attemptsMode, bucket],
  );
  // These functions only need mode-filtered runs — pass filteredRuns with mode "all"
  // so their internal applyMode is a no-op (identity return, no re-filter).
  const runsOverTime = useMemo(
    () => computeRunsOverTime(filteredRuns, workflows, "all", bucket),
    [filteredRuns, workflows, bucket],
  );
  const successTrend = useMemo(
    () => computeSuccessRateTrend(filteredRuns, "all", bucket),
    [filteredRuns, bucket],
  );
  const durationTrend = useMemo(
    () => computeDurationTrend(filteredRuns, "all", bucket),
    [filteredRuns, bucket],
  );
  const failure = useMemo(
    () => computeFailureBreakdown(filteredRuns, workflows, "all"),
    [filteredRuns, workflows],
  );
  const heatmap = useMemo(
    () => computeActivityHeatmap(filteredRuns, utc, "all"),
    [filteredRuns, utc],
  );
  const triggers = useMemo(
    () => computeTriggerEvents(filteredRuns, "all"),
    [filteredRuns],
  );
  const runnerOS = useMemo(
    () => computeRunnerOSBreakdown(timings ?? []),
    [timings],
  );
  const queueTimeTrend = useMemo(
    () => computeQueueTimeTrend(runs, bucket),
    [runs, bucket],
  );
  const branchStats = useMemo(
    () => computeBranchStats(filteredRuns, "all", 10),
    [filteredRuns],
  );
  // Enumerate every bucket in the selected window so the matrix shows empty
  // weeks as zero columns instead of collapsing them out of the table.
  const allPeriods = useMemo(
    () => enumeratePeriods(runsQ.windowStart, runsQ.windowEnd, bucket),
    [runsQ.windowStart, runsQ.windowEnd, bucket],
  );
  const evolutionMatrix = useMemo(
    () => computeEvolutionMatrix(runsOverTime, workflowStats, 10, allPeriods),
    [runsOverTime, workflowStats, allPeriods],
  );

  // ─── Compare analytics (period B) ─────────────────────────────────────────
  const prevRuns = prevRunsQ.runs ?? EMPTY_RUNS;
  const prevFilteredRuns = useMemo(
    () => (attemptsMode === "first-pass" ? prevRuns.filter(isFirstPass) : prevRuns),
    [prevRuns, attemptsMode],
  );

  const compareOverview = useMemo(
    () => (compareMode && prevRuns.length > 0 ? computeOverview(prevRuns, attemptsMode) : null),
    [compareMode, prevRuns, attemptsMode],
  );
  const compareWorkflowStats = useMemo(
    () =>
      compareMode && prevRuns.length > 0
        ? computeWorkflowStats(prevRuns, workflows, attemptsMode, bucket)
        : null,
    [compareMode, prevRuns, workflows, attemptsMode, bucket],
  );
  const compareRunsOverTime = useMemo(
    () =>
      compareMode && prevFilteredRuns.length > 0
        ? computeRunsOverTime(prevFilteredRuns, workflows, "all", bucket)
        : null,
    [compareMode, prevFilteredRuns, workflows, bucket],
  );
  const compareSuccessTrend = useMemo(
    () =>
      compareMode && prevFilteredRuns.length > 0
        ? computeSuccessRateTrend(prevFilteredRuns, "all", bucket)
        : null,
    [compareMode, prevFilteredRuns, bucket],
  );
  const compareDurationTrend = useMemo(
    () =>
      compareMode && prevFilteredRuns.length > 0
        ? computeDurationTrend(prevFilteredRuns, "all", bucket)
        : null,
    [compareMode, prevFilteredRuns, bucket],
  );
  const compareQueueTimeTrend = useMemo(
    () =>
      compareMode && prevRuns.length > 0
        ? computeQueueTimeTrend(prevRuns, bucket)
        : null,
    [compareMode, prevRuns, bucket],
  );
  // ──────────────────────────────────────────────────────────────────────────

  const slowestJobs = useMemo(() => {
    if (!perRunJobs) return [];
    const wfNameById = new Map(workflows.map((w) => [w.id, w.name ?? w.path ?? null]));
    const flat = perRunJobs.flatMap(({ run, jobs }) =>
      jobs.map((job) => ({
        job,
        run,
        workflowName:
          (typeof run.workflow_id === "number"
            ? wfNameById.get(run.workflow_id)
            : null) ?? null,
      })),
    );
    return computeSlowestJobs(flat, 10);
  }, [perRunJobs, workflows]);

  const isInitialLoading = runsQ.isLoading || workflowsQ.isLoading;

  const lowBudget = rateLimit.data?.lowBudget === true;

  // Rendered in every state (error, loading, empty, data) so the user is never
  // trapped — they can always change the window or toggle re-runs.
  const controlsBar = (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <Header owner={owner} repo={name} windowDays={windowDays} compareMode={compareMode} />
      <div className="flex items-center gap-4 flex-wrap">
        <DateRangePicker value={windowDays} onChange={setWindowDays} />
        <BucketToggle value={bucketPref} onChange={setBucketPref} />
        <AttemptsModeToggle value={attemptsMode} onChange={setAttemptsMode} />
        <CompareToggle
          value={compareMode}
          onChange={setCompareMode}
          disabled={lowBudget}
          disabledReason="Compare uses 2× rate-limit budget, enable manually"
        />
      </div>
    </div>
  );

  if (runsQ.isError) {
    const status = (runsQ.error as { status?: number } | null)?.status;
    const tone = status === 404 || status === 403 ? "no-access" : "error";
    return (
      <div className="container mx-auto px-4 py-8 space-y-4">
        {controlsBar}
        <EmptyState
          tone={tone}
          title={
            status === 404
              ? "Repository not found"
              : status === 403
                ? "No access to this repository"
                : "Failed to load runs"
          }
          description="Try a different repo or sign in with a token that has access."
          action={
            <Button variant="outline" onClick={() => navigate("/pick")}>
              Pick another repo
            </Button>
          }
        />
      </div>
    );
  }

  if (isInitialLoading) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-4">
        {controlsBar}
        {rateLimit.data ? <RateLimitBanner summary={rateLimit.data} onRetry={() => runsQ /* noop */} /> : null}
        <ProgressBanner
          loaded={runsQ.loaded}
          total={runsQ.total}
          currentDate={runsQ.currentDate}
        />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  // Previous-period fetch may still be loading after current period resolves.
  const isPrevLoading = compareMode && prevRunsQ.isLoading;

  if (runs.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-4">
        {controlsBar}
        <EmptyState
          title={workflows.length === 0 ? "No Actions configured" : "No runs in window"}
          description={
            workflows.length === 0
              ? "This repository does not use GitHub Actions yet."
              : `There are no workflow runs in the last ${windowDays} days. Try a wider window.`
          }
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {controlsBar}

      {rateLimit.data ? <RateLimitBanner summary={rateLimit.data} onRetry={() => runsQ /* noop */} /> : null}
      {isPrevLoading ? (
        <ProgressBanner
          loaded={prevRunsQ.loaded}
          total={prevRunsQ.total}
          currentDate={prevRunsQ.currentDate}
          label="Loading previous period"
        />
      ) : null}
      <CapWarningBanner
        hitCap={runsQ.hitCap || (compareMode && prevRunsQ.hitCap)}
        hardCap={runsQ.hardCap}
        windowDays={windowDays}
        collected={runs.length}
      />

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="failures">Failures</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="branches">Branches</TabsTrigger>
          <TabsTrigger value="evolution">Evolution</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <OverviewCards
            overview={overview}
            compare={compareOverview ?? undefined}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <RunsOverTime
              rows={runsOverTime}
              workflows={workflowStats}
              bucket={bucket}
              compareRows={compareRunsOverTime ?? undefined}
            />
            <SuccessRateTrend
              rows={successTrend}
              bucket={bucket}
              compareRows={compareSuccessTrend ?? undefined}
            />
          </div>
          <DurationTrend
            rows={durationTrend}
            bucket={bucket}
            compareRows={compareDurationTrend ?? undefined}
          />
        </TabsContent>

        <TabsContent value="workflows" className="space-y-4">
          <WorkflowsTable
            rows={workflowStats}
            compareStats={compareWorkflowStats ?? undefined}
          />
        </TabsContent>

        <TabsContent value="failures" className="space-y-4">
          <FailureAnalysis breakdown={failure} />
          <SlowestJobs
            jobs={slowestJobs}
            loading={lazyLoading}
            loaded={perRunJobs != null}
            onLoad={loadHeavyData}
            disabled={paused}
          />
        </TabsContent>

        <TabsContent value="activity" className="space-y-4">
          <ActivityHeatmap cells={heatmap} utc={utc} onToggleTz={() => setUtc((u) => !u)} />
          <TriggerEventsChart rows={triggers} />
        </TabsContent>

        <TabsContent value="branches" className="space-y-4">
          {branchStats.length === 0 ? (
            <EmptyState title="No branches found in this window" />
          ) : (
            <BranchBreakdown rows={branchStats} />
          )}
        </TabsContent>

        <TabsContent value="evolution" className="space-y-4">
          {evolutionMatrix.periods.length === 0 || evolutionMatrix.rows.length === 0 ? (
            <EmptyState title="No data in this window" />
          ) : (
            <EvolutionMatrix matrix={evolutionMatrix} />
          )}
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <QueueTimeTrend
            rows={queueTimeTrend}
            bucket={bucket}
            compareRows={compareQueueTimeTrend ?? undefined}
          />
        </TabsContent>

        <TabsContent value="billing" className="space-y-4">
          <RunnerOSBreakdownChart breakdown={runnerOS} loading={lazyLoading} />
          {timings == null ? (
            <div className="flex justify-end">
              <Button size="sm" onClick={loadHeavyData} disabled={paused || lazyLoading}>
                {lazyLoading ? "Loading…" : "Load billing data"}
              </Button>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface HeaderProps {
  owner: string;
  repo: string;
  windowDays?: WindowDays;
  compareMode?: boolean;
}

function Header({ owner, repo, windowDays = 7, compareMode = false }: HeaderProps) {
  const navigate = useNavigate();
  const subtitle = compareMode
    ? `Last ${windowDays} days vs previous ${windowDays} days · data pulled directly from api.github.com`
    : `Last ${windowDays} days · data pulled directly from api.github.com`;
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold">
          {owner}/{repo}
        </h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate("/pick")}>
          <RefreshCw className="size-4 mr-2" />
          Change repo
        </Button>
      </div>
    </div>
  );
}
